/* Cookie consent helper (#2255 — GDPR + ePrivacy cookie banner).
 *
 * One source of truth for "what categories has the user opted into" so the
 * banner, the cookies page, and (in future) analytics scripts agree.
 *
 * Storage shape (localStorage key `2hives_cookie_consent`):
 *   {
 *     v: 1,                       // bump on policy revision -> re-prompt
 *     essential: true,            // always true (legitimate interest)
 *     analytics: boolean,
 *     ts: ISO-8601 timestamp,
 *   }
 *
 * Bump CONSENT_VERSION when our cookie policy materially changes — older
 * stored choices become invalid and the banner reappears on next load.
 */

export const CONSENT_VERSION = 1;
const CONSENT_KEY = "2hives_cookie_consent";

export interface CookieConsent {
  v:          number;
  essential:  true;
  analytics:  boolean;
  ts:         string;
}

export function getCookieConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (!parsed || parsed.v !== CONSENT_VERSION) return null;
    return {
      v:         CONSENT_VERSION,
      essential: true,
      analytics: Boolean(parsed.analytics),
      ts:        typeof parsed.ts === "string" ? parsed.ts : new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function setCookieConsent(analytics: boolean): CookieConsent {
  const c: CookieConsent = {
    v:         CONSENT_VERSION,
    essential: true,
    analytics,
    ts:        new Date().toISOString(),
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(c)); } catch { /* quota or privacy mode */ }
  return c;
}

export function clearCookieConsent(): void {
  try { localStorage.removeItem(CONSENT_KEY); } catch { /* noop */ }
}
